import { Busboy } from '@fastify/busboy';
import type { ToolsInput } from '@mastra/core/agent';
import type { Mastra } from '@mastra/core/mastra';
import type { RequestContext } from '@mastra/core/request-context';
import type { InMemoryTaskStore } from '@mastra/server/a2a/store';
import { findMatchingCustomRoute, isProtectedCustomRoute } from '@mastra/server/auth';
import type { MCPHttpTransportResult, MCPSseTransportResult } from '@mastra/server/handlers/mcp';
import type { ParsedRequestParams, ServerRoute } from '@mastra/server/server-adapter';
import {
  MastraServer as MastraServerBase,
  checkRouteFGA,
  getCustomHTTPExceptionResponse,
  isSafeObjectKey,
  isZodError,
  normalizeQueryParams,
  redactStreamChunk,
  serializeStreamChunk,
} from '@mastra/server/server-adapter';
import type Koa from 'koa';
import type { Context, Middleware, Next } from 'koa';
export { createAuthMiddleware } from './auth-middleware';
export type { KoaAuthMiddlewareOptions } from './auth-middleware';

type HasPermissionFn = (userPerms: string[], required: string) => boolean;
type RegisteredKoaRoute = {
  route: ServerRoute;
  prefix: string;
  koaPath: string;
  pathRegex: RegExp;
  paramNames: string[];
};
type RouteDispatcherGroup = {
  routes: RegisteredKoaRoute[];
  stackLengthAfterRegistration: number;
};
let _hasPermissionPromise: Promise<HasPermissionFn | undefined> | undefined;
function loadHasPermission(): Promise<HasPermissionFn | undefined> {
  if (!_hasPermissionPromise) {
    _hasPermissionPromise = import('@mastra/core/auth/ee')
      .then(m => m.hasPermission)
      .catch(() => {
        console.error(
          '[@mastra/koa] Auth features require @mastra/core >= 1.6.0. Please upgrade: npm install @mastra/core@latest',
        );
        return undefined;
      });
  }
  return _hasPermissionPromise;
}

/**
 * Convert Koa context to Web API Request for cookie-based auth providers.
 */
function toWebRequest(ctx: Context): globalThis.Request {
  const protocol = ctx.protocol || 'http';
  const host = ctx.host || 'localhost';
  const url = `${protocol}://${host}${ctx.url}`;

  const headers = new Headers();
  for (const [key, value] of Object.entries(ctx.headers)) {
    if (value) {
      if (Array.isArray(value)) {
        value.forEach(v => headers.append(key, v));
      } else {
        headers.set(key, value);
      }
    }
  }

  return new globalThis.Request(url, {
    method: ctx.method,
    headers,
  });
}

// Extend Koa types to include Mastra context
declare module 'koa' {
  interface DefaultState {
    mastra: Mastra;
    requestContext: RequestContext;
    tools: ToolsInput;
    abortSignal: AbortSignal;
    taskStore: InMemoryTaskStore;
    customRouteAuthConfig?: Map<string, boolean>;
  }
  interface Request {
    body?: unknown;
  }
}

export class MastraServer extends MastraServerBase<Koa, Context, Context> {
  private readonly activeRouteDispatchers = new WeakMap<Koa, RouteDispatcherGroup>();

  async init() {
    this.registerErrorMiddleware();
    await super.init();
  }

  /**
   * Register a global error-handling middleware at the top of the middleware chain.
   * This acts as a safety net for errors that propagate past route handlers
   * (e.g., from auth middleware, context middleware, or when route handlers re-throw).
   *
   * When `server.onError` is configured, calls it and uses the response.
   * Otherwise provides a default JSON error response.
   *
   * Errors are emitted on the app for logging (Koa convention) but NOT re-thrown,
   * so this middleware is the final error boundary. Users who need custom error handling
   * should use `server.onError` or register their own middleware between this and the routes.
   */
  private registerErrorMiddleware(): void {
    const server = this;

    this.app.use(async function mastraErrorBoundary(ctx: Context, next: Next) {
      try {
        await next();
      } catch (err) {
        // Try onError first (may have already been called in registerRoute,
        // but this catches errors from other middleware too)
        if (await server.handleOnError(err, ctx)) {
          return;
        }

        // Default error handling
        const error = err instanceof Error ? err : new Error(String(err));
        let status = 500;
        if (err && typeof err === 'object') {
          if ('status' in err) {
            status = (err as any).status;
          } else if (
            'details' in err &&
            (err as any).details &&
            typeof (err as any).details === 'object' &&
            'status' in (err as any).details
          ) {
            status = (err as any).details.status;
          }
        }
        ctx.status = status;
        ctx.body = { error: error.message || 'Unknown error' };

        // The route handler already logged this 501 at warn level. Skip the emit when only
        // Koa's default listener would receive it, since that prints to console.error.
        // Apps that register their own 'error' listeners still get the event.
        const loggedAsWarning = status === 501 && (ctx as any)._mastraLoggedNotImplementedError === err;
        const onlyDefaultListener = ctx.app.listeners('error').every(listener => listener === ctx.app.onerror);
        if (loggedAsWarning && onlyDefaultListener) {
          return;
        }

        // Emit the error for logging (standard Koa pattern) but don't re-throw
        // since this middleware is the final error boundary.
        ctx.app.emit('error', err, ctx);
      }
    });
  }

  /**
   * Try to handle an error using the `server.onError` hook.
   * Creates a minimal context shim compatible with the Hono-style onError signature.
   *
   * @returns true if the error was handled and the response was set on ctx
   */
  private async handleOnError(err: unknown, ctx: Context): Promise<boolean> {
    // Guard against double invocation (route catch → re-throw → error middleware)
    if ((ctx as any)._mastraOnErrorAttempted) return false;
    (ctx as any)._mastraOnErrorAttempted = true;

    const onError = this.mastra.getServer()?.onError;
    if (!onError) return false;

    const error = err instanceof Error ? err : new Error(String(err));

    // Create a minimal context shim compatible with the onError signature
    const shimContext = {
      json: (data: unknown, status: number = 200) =>
        new Response(JSON.stringify(data), {
          status,
          headers: { 'Content-Type': 'application/json' },
        }),
      req: {
        path: ctx.path,
        method: ctx.method,
        header: (name: string) => {
          const value = ctx.headers[name.toLowerCase()];
          if (Array.isArray(value)) return value.join(', ');
          return value;
        },
        url: ctx.url,
      },
    };

    try {
      const response = await onError(error, shimContext as any);
      // Apply the Response from onError to the Koa context
      ctx.status = response.status;
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        ctx.body = await response.json();
      } else {
        ctx.body = await response.text();
      }
      return true;
    } catch (onErrorErr) {
      this.mastra.getLogger()?.error('Error in custom onError handler', {
        error: onErrorErr instanceof Error ? { message: onErrorErr.message, stack: onErrorErr.stack } : onErrorErr,
      });
      return false;
    }
  }

  createContextMiddleware(): Middleware {
    const server = this;

    return async function mastraRequestContext(ctx: Context, next: Next) {
      // Parse request context from request body and add to context
      let bodyRequestContext: Record<string, any> | undefined;
      let paramsRequestContext: Record<string, any> | undefined;

      // Parse request context from request body (POST/PUT)
      if (ctx.method === 'POST' || ctx.method === 'PUT') {
        const contentType = ctx.headers['content-type'];
        if (contentType?.includes('application/json') && ctx.request.body) {
          const body = ctx.request.body as { requestContext?: Record<string, any> };
          if (body.requestContext) {
            bodyRequestContext = body.requestContext;
          }
        }
      }

      // Parse request context from query params (GET)
      if (ctx.method === 'GET') {
        try {
          const query = ctx.query;
          const encodedRequestContext = query.requestContext;
          if (typeof encodedRequestContext === 'string') {
            // Try JSON first
            try {
              paramsRequestContext = JSON.parse(encodedRequestContext);
            } catch {
              // Fallback to base64(JSON)
              try {
                const json = Buffer.from(encodedRequestContext, 'base64').toString('utf-8');
                paramsRequestContext = JSON.parse(json);
              } catch {
                // ignore if still invalid
              }
            }
          }
        } catch {
          // ignore query parsing errors
        }
      }

      const requestContext = server.mergeRequestContext({ paramsRequestContext, bodyRequestContext });
      server.applyRequestMetadataToContext({
        requestContext,
        getHeader: name => ctx.get(name),
      });

      // Set context in state object
      ctx.state.requestContext = requestContext;
      ctx.state.mastra = server.mastra;
      ctx.state.tools = server.tools || {};
      if (server.taskStore) {
        ctx.state.taskStore = server.taskStore;
      }
      ctx.state.customRouteAuthConfig = server.customRouteAuthConfig;

      // Create abort controller for request cancellation
      const controller = new AbortController();
      ctx.req.on('close', () => {
        // Only abort if the response wasn't successfully completed
        if (!ctx.res.writableEnded) {
          controller.abort();
        }
      });
      ctx.state.abortSignal = controller.signal;

      await next();
    };
  }

  private getRouteDispatcherGroup(app: Koa): RouteDispatcherGroup {
    // The dispatcher-reuse optimization needs to observe app.middleware.length
    // to detect when other middleware was registered between our route
    // registrations (in which case we must start a new dispatcher group to
    // preserve middleware ordering). Subclasses may pass an app-like object
    // (e.g., a koa-router or a mounted sub-app) that only exposes `use` and
    // has no `middleware` array. In that case, skip reuse and register a fresh
    // dispatcher per call — equivalent to the pre-1.5.0 per-route behavior.
    const middlewareStack = (app as { middleware?: unknown }).middleware;
    const supportsReuse = Array.isArray(middlewareStack);

    if (supportsReuse) {
      const activeGroup = this.activeRouteDispatchers.get(app);
      if (activeGroup && middlewareStack.length === activeGroup.stackLengthAfterRegistration) {
        return activeGroup;
      }
    }

    const group: RouteDispatcherGroup = {
      routes: [],
      stackLengthAfterRegistration: 0,
    };
    app.use(this.createRouteDispatcherMiddleware(group));

    if (supportsReuse) {
      group.stackLengthAfterRegistration = (app as { middleware: unknown[] }).middleware.length;
      this.activeRouteDispatchers.set(app, group);
    }

    return group;
  }

  private createRouteDispatcherMiddleware(group: RouteDispatcherGroup): Middleware {
    const server = this;

    return async function mastraRouteDispatcher(ctx: Context, next: Next) {
      const matchedRoute = server.findRegisteredRoute(group.routes, ctx);

      if (!matchedRoute) {
        await next();
        return;
      }

      await server.handleMatchedRoute(matchedRoute, ctx);
    };
  }

  private findRegisteredRoute(routes: RegisteredKoaRoute[], ctx: Context): RegisteredKoaRoute | undefined {
    const method = ctx.method.toUpperCase();

    for (const registeredRoute of routes) {
      if (
        registeredRoute.route.method.toUpperCase() !== 'ALL' &&
        method !== registeredRoute.route.method.toUpperCase()
      ) {
        continue;
      }

      const match = registeredRoute.pathRegex.exec(ctx.path);
      if (!match) {
        continue;
      }

      ctx.params = {};
      registeredRoute.paramNames.forEach((name, index) => {
        const value = match[index + 1];
        ctx.params[name] = value === undefined ? value : decodeURIComponent(value);
      });

      return registeredRoute;
    }

    return undefined;
  }

  private async handleMatchedRoute(registeredRoute: RegisteredKoaRoute, ctx: Context): Promise<void> {
    const { route, prefix } = registeredRoute;

    const authError = await this.checkRouteAuth(route, {
      path: String(ctx.path || '/'),
      method: String(ctx.method || 'GET'),
      getHeader: name => ctx.headers[name.toLowerCase()] as string | undefined,
      getQuery: name => (ctx.query as Record<string, string>)[name],
      requestContext: ctx.state.requestContext,
      request: toWebRequest(ctx),
      buildAuthorizeContext: () => toWebRequest(ctx),
    });

    if (authError) {
      // Apply any refresh headers (e.g. Set-Cookie from transparent session refresh)
      if (authError.headers) {
        for (const [key, value] of Object.entries(authError.headers)) {
          ctx.set(key, value);
        }
      }

      // If this is an auth error (not just a success-with-headers), return error response
      if (authError.error) {
        ctx.status = authError.status;
        ctx.body = { error: authError.error };
        return;
      }
    }

    const params = await this.getParams(route, ctx);

    // Return 400 Bad Request if body parsing failed (e.g., malformed multipart data)
    if (params.bodyParseError) {
      ctx.status = 400;
      ctx.body = {
        error: 'Invalid request body',
        issues: [{ field: 'body', message: params.bodyParseError.message }],
      };
      return;
    }

    if (params.queryParams) {
      try {
        params.queryParams = await this.parseQueryParams(route, params.queryParams);
      } catch (error) {
        this.mastra.getLogger()?.error('Error parsing query params', {
          error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
        });
        if (isZodError(error)) {
          const resolved = this.resolveValidationError(route, error, 'query');
          ctx.status = resolved.status;
          ctx.body = resolved.body;
          return;
        }
        ctx.status = 400;
        ctx.body = {
          error: 'Invalid query parameters',
          issues: [{ field: 'unknown', message: error instanceof Error ? error.message : 'Unknown error' }],
        };
        return;
      }
    }

    if (params.body !== undefined || route.bodySchema) {
      try {
        params.body = await this.parseBody(route, params.body);
      } catch (error) {
        this.mastra.getLogger()?.error('Error parsing body', {
          error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
        });
        if (isZodError(error)) {
          const resolved = this.resolveValidationError(route, error, 'body');
          ctx.status = resolved.status;
          ctx.body = resolved.body;
          return;
        }
        ctx.status = 400;
        ctx.body = {
          error: 'Invalid request body',
          issues: [{ field: 'unknown', message: error instanceof Error ? error.message : 'Unknown error' }],
        };
        return;
      }
    }

    // Parse path params through pathParamSchema for type coercion (e.g., z.coerce.number())
    if (params.urlParams) {
      try {
        params.urlParams = await this.parsePathParams(route, params.urlParams);
      } catch (error) {
        this.mastra.getLogger()?.error('Error parsing path params', {
          error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
        });
        if (isZodError(error)) {
          const resolved = this.resolveValidationError(route, error, 'path');
          ctx.status = resolved.status;
          ctx.body = resolved.body;
          return;
        }
        ctx.status = 400;
        ctx.body = {
          error: 'Invalid path parameters',
          issues: [{ field: 'unknown', message: error instanceof Error ? error.message : 'Unknown error' }],
        };
        return;
      }
    }

    const handlerParams = {
      ...params.urlParams,
      ...params.queryParams,
      ...(typeof params.body === 'object' ? params.body : {}),
      requestContext: ctx.state.requestContext,
      mastra: this.mastra,
      tools: ctx.state.tools,
      taskStore: ctx.state.taskStore,
      abortSignal: ctx.state.abortSignal,
      routePrefix: prefix,
      request: toWebRequest(ctx),
    };

    // Check route permission requirement (EE feature)
    // Uses convention-based permission derivation: permissions are auto-derived
    // from route path/method unless explicitly set or route is public
    const requestContext = ctx.state.requestContext;
    // Check if any auth is configured (studio or server) for RBAC
    const hasAuth = this.mastra.getStudio?.()?.auth || this.mastra.getServer()?.auth;
    if (hasAuth) {
      const hasPermission = await loadHasPermission();
      if (hasPermission) {
        const userPermissions = requestContext.get('mastra__userPermissions') as string[] | undefined;
        const permissionError = this.checkRoutePermission(route, userPermissions, hasPermission, requestContext);

        if (permissionError) {
          ctx.status = permissionError.status;
          ctx.body = {
            error: permissionError.error,
            message: permissionError.message,
          };
          return;
        }
      }
    }

    // Check FGA authorization (EE feature)
    const fgaError = await checkRouteFGA(this.mastra, route, requestContext, {
      ...params.urlParams,
      ...params.queryParams,
      ...(typeof params.body === 'object' ? params.body : {}),
    });
    if (fgaError) {
      ctx.status = fgaError.status;
      ctx.body = { error: fgaError.error, message: fgaError.message };
      return;
    }

    try {
      const result = await route.handler(handlerParams);
      await this.sendResponse(route, ctx, result, prefix);
    } catch (error) {
      const httpStatus =
        error && typeof error === 'object' ? ((error as any).status ?? (error as any).details?.status) : undefined;
      const isClientError = typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500;
      if (!isClientError) {
        // 501 means an optional capability isn't provided by the configured storage or core: expected, not a server fault.
        const logLevel = httpStatus === 501 ? 'warn' : 'error';
        this.mastra.getLogger()?.[logLevel]('Error calling handler', {
          error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
          path: route.path,
          method: route.method,
        });
        (ctx as any)._mastraLoggedNotImplementedError = httpStatus === 501 ? error : undefined;
      }
      const customResponse = getCustomHTTPExceptionResponse(error);
      if (customResponse) {
        ctx.status = customResponse.status;
        customResponse.headers.forEach((value, name) => ctx.set(name, value));
        ctx.body = Buffer.from(await customResponse.arrayBuffer());
        return;
      }

      // Attach status code to the error for upstream middleware
      if (error && typeof error === 'object') {
        if (!('status' in error)) {
          // Check for MastraError with status in details
          if ('details' in error && error.details && typeof error.details === 'object' && 'status' in error.details) {
            (error as any).status = (error.details as any).status;
          }
        }
      }

      // Try to call server.onError if configured
      if (await this.handleOnError(error, ctx)) {
        return;
      }

      // Re-throw so the error propagates up Koa's middleware chain
      throw error;
    }
  }

  async stream(route: ServerRoute, ctx: Context, result: { fullStream: ReadableStream }): Promise<void> {
    // Tell Koa we're handling the response ourselves
    ctx.respond = false;

    const streamFormat = route.streamFormat || 'stream';

    // Set status and headers via ctx.res directly since we're bypassing Koa's response
    const sseHeaders =
      streamFormat === 'sse'
        ? {
            'Content-Type': 'text/event-stream',
            'Cache-Control': 'no-cache',
            Connection: 'keep-alive',
            'X-Accel-Buffering': 'no',
          }
        : {
            'Content-Type': 'text/plain',
          };

    ctx.res.writeHead(200, {
      ...sseHeaders,
      'Transfer-Encoding': 'chunked',
    });

    if (streamFormat === 'sse' && route.sseFlushOnConnect) {
      ctx.res.write(': connected\n\n');
    }

    const readableStream = result instanceof ReadableStream ? result : result.fullStream;
    const reader = readableStream.getReader();

    ctx.res.on('close', () => {
      void reader.cancel('request aborted').catch(() => {});
    });

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (value) {
          if (streamFormat === 'sse' && typeof value === 'string' && value.startsWith(':')) {
            ctx.res.write(value);
            continue;
          }

          // Optionally redact sensitive data (system prompts, tool definitions, API keys) before sending to the client
          const shouldRedact = this.streamOptions?.redact ?? true;
          const outputValue = shouldRedact ? redactStreamChunk(value) : value;
          // A chunk that can't be serialized must not kill the stream — skip it and keep streaming
          const serialized = serializeStreamChunk(outputValue);
          if (!serialized.ok) {
            this.mastra.getLogger()?.error('Failed to serialize stream chunk, skipping', {
              path: route.path,
              chunkType: (outputValue as { type?: string })?.type,
              error: serialized.error.message,
            });
            continue;
          }
          if (streamFormat === 'sse') {
            ctx.res.write(`data: ${serialized.json}\n\n`);
          } else {
            ctx.res.write(serialized.json + '\x1E');
          }
        }
      }
    } catch (error) {
      this.mastra.getLogger()?.error('Error in stream processing', {
        error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
      });
    } finally {
      ctx.res.end();
    }
  }

  async getParams(route: ServerRoute, ctx: Context): Promise<ParsedRequestParams> {
    const urlParams = (ctx.params || {}) as Record<string, string>;
    // Koa's ctx.query is ParsedUrlQuery which is Record<string, string | string[]>
    const queryParams = normalizeQueryParams((ctx.query || {}) as Record<string, unknown>);
    let body: unknown;
    let bodyParseError: { message: string } | undefined;

    if (route.method === 'POST' || route.method === 'PUT' || route.method === 'PATCH' || route.method === 'DELETE') {
      const contentType = ctx.headers['content-type'] || '';

      if (contentType.includes('multipart/form-data')) {
        try {
          const maxFileSize = route.maxBodySize ?? this.bodyLimitOptions?.maxSize;
          body = await this.parseMultipartFormData(ctx, maxFileSize);
        } catch (error) {
          this.mastra.getLogger()?.error('Failed to parse multipart form data', {
            error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
          });
          // Re-throw size limit errors, let others fall through to validation
          if (error instanceof Error && error.message.toLowerCase().includes('size')) {
            throw error;
          }
          bodyParseError = {
            message: error instanceof Error ? error.message : 'Failed to parse multipart form data',
          };
        }
      } else {
        body = ctx.request.body;
      }
    }

    return { urlParams, queryParams, body, bodyParseError };
  }

  /**
   * Parse multipart/form-data using @fastify/busboy.
   * Converts file uploads to Buffers and parses JSON field values.
   *
   * @param ctx - The Koa context object
   * @param maxFileSize - Optional maximum file size in bytes
   */
  private parseMultipartFormData(ctx: Context, maxFileSize?: number): Promise<Record<string, unknown>> {
    return new Promise((resolve, reject) => {
      const result: Record<string, unknown> = {};

      const busboy = new Busboy({
        headers: {
          'content-type': ctx.headers['content-type'] as string,
        },
        limits: maxFileSize ? { fileSize: maxFileSize } : undefined,
      });

      busboy.on('file', (fieldname: string, file: NodeJS.ReadableStream) => {
        const chunks: Buffer[] = [];
        let limitExceeded = false;

        file.on('data', (chunk: Buffer) => {
          chunks.push(chunk);
        });

        file.on('limit', () => {
          limitExceeded = true;
          reject(new Error(`File size limit exceeded${maxFileSize ? ` (max: ${maxFileSize} bytes)` : ''}`));
        });

        file.on('end', () => {
          if (!limitExceeded && isSafeObjectKey(fieldname)) {
            result[fieldname] = Buffer.concat(chunks);
          }
        });
      });

      busboy.on('field', (fieldname: string, value: string) => {
        if (!isSafeObjectKey(fieldname)) {
          return;
        }
        // Try to parse JSON strings (like 'options')
        try {
          result[fieldname] = JSON.parse(value);
        } catch {
          result[fieldname] = value;
        }
      });

      busboy.on('finish', () => {
        resolve(result);
      });

      busboy.on('error', (error: Error) => {
        reject(error);
      });

      // Pipe the raw request to busboy
      ctx.req.pipe(busboy);
    });
  }

  async sendResponse(route: ServerRoute, ctx: Context, result: unknown, prefix?: string): Promise<void> {
    const resolvedPrefix = prefix ?? this.prefix ?? '';

    // Apply refresh headers from transparent session refresh (e.g. Set-Cookie after token refresh)
    if (result && typeof result === 'object' && '__refreshHeaders' in result) {
      const refreshHeaders = (result as any).__refreshHeaders as Record<string, string>;
      for (const [key, value] of Object.entries(refreshHeaders)) {
        ctx.set(key, value);
      }
      delete (result as any).__refreshHeaders;
    }

    if (route.responseType === 'json') {
      // Explicitly set content-type and handle null/undefined to ensure proper JSON response
      // Koa sets 204 No Content when body is null, but we want to return JSON null
      ctx.type = 'application/json';
      ctx.body = result === null || result === undefined ? JSON.stringify(null) : result;
    } else if (route.responseType === 'stream') {
      await this.stream(route, ctx, result as { fullStream: ReadableStream });
    } else if (route.responseType === 'datastream-response') {
      // Handle AI SDK Response objects - pipe Response.body to Koa response
      // Tell Koa we're handling the response ourselves
      ctx.respond = false;

      const fetchResponse = result as globalThis.Response;
      const headers: Record<string, string> = {};
      fetchResponse.headers.forEach((value, key) => {
        headers[key] = value;
      });
      ctx.res.writeHead(fetchResponse.status, headers);

      if (fetchResponse.body) {
        const reader = fetchResponse.body.getReader();

        const onResError = (err: unknown) => {
          this.mastra.getLogger()?.error('Error writing datastream response', {
            error: err instanceof Error ? { message: err.message, stack: err.stack } : err,
          });
          void reader.cancel('response write error').catch(() => {});
        };
        ctx.res.once('error', onResError);

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            ctx.res.write(value);
          }
        } catch (error) {
          this.mastra.getLogger()?.error('Error in datastream processing', {
            error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
          });
        } finally {
          ctx.res.off('error', onResError);
          ctx.res.end();
        }
      } else {
        ctx.res.end();
      }
    } else if (route.responseType === 'mcp-http') {
      // MCP Streamable HTTP transport
      // Tell Koa we're handling the response ourselves
      ctx.respond = false;

      const { server, httpPath, mcpOptions: routeMcpOptions } = result as MCPHttpTransportResult;

      try {
        // Attach parsed body to raw request so MCP server's readJsonBody can use it
        const rawReq = ctx.req as typeof ctx.req & { body?: unknown };
        if (ctx.request.body !== undefined) {
          rawReq.body = ctx.request.body;
        }

        // Merge class-level mcpOptions with route-specific options (route takes precedence)
        const options = { ...this.mcpOptions, ...routeMcpOptions };

        await server.startHTTP({
          url: new URL(ctx.url, `http://${ctx.headers.host}`),
          httpPath: `${resolvedPrefix}${httpPath}`,
          req: rawReq,
          res: ctx.res,
          options: Object.keys(options).length > 0 ? options : undefined,
        });
        // Response handled by startHTTP
      } catch {
        if (!ctx.res.headersSent) {
          ctx.res.writeHead(500, { 'Content-Type': 'application/json' });
          ctx.res.end(
            JSON.stringify({
              jsonrpc: '2.0',
              error: { code: -32603, message: 'Internal server error' },
              id: null,
            }),
          );
        }
      }
    } else if (route.responseType === 'mcp-sse') {
      // MCP SSE transport
      // Tell Koa we're handling the response ourselves
      ctx.respond = false;

      const { server, ssePath, messagePath } = result as MCPSseTransportResult;

      try {
        // Attach parsed body to raw request so MCP server's readJsonBody can use it
        const rawReq = ctx.req as typeof ctx.req & { body?: unknown };
        if (ctx.request.body !== undefined) {
          rawReq.body = ctx.request.body;
        }

        await server.startSSE({
          url: new URL(ctx.url, `http://${ctx.headers.host}`),
          ssePath: `${resolvedPrefix}${ssePath}`,
          messagePath: `${resolvedPrefix}${messagePath}`,
          req: rawReq,
          res: ctx.res,
        });
        // Response handled by startSSE
      } catch {
        if (!ctx.res.headersSent) {
          ctx.res.writeHead(500, { 'Content-Type': 'application/json' });
          ctx.res.end(JSON.stringify({ error: 'Error handling MCP SSE request' }));
        }
      }
    } else {
      ctx.status = 500;
    }
  }

  async registerRoute(app: Koa, route: ServerRoute, { prefix: prefixParam }: { prefix?: string } = {}): Promise<void> {
    // Default prefix to this.prefix if not provided, or empty string
    const prefix = prefixParam ?? this.prefix ?? '';

    const fullPath = `${prefix}${route.path}`;

    // Convert Express-style :param to Koa-style :param (they're the same)
    const koaPath = fullPath;

    const group = this.getRouteDispatcherGroup(app);
    group.routes.push({
      route,
      prefix,
      koaPath,
      pathRegex: this.pathToRegex(koaPath),
      paramNames: this.extractParamNames(koaPath),
    });
  }

  /**
   * Convert Express-style path to regex for matching
   */
  private pathToRegex(path: string): RegExp {
    // First replace :param with a placeholder that won't be affected by escaping
    const PARAM_PLACEHOLDER = '\x00PARAM\x00';
    const pathWithPlaceholders = path.replace(/:[^/]+/g, PARAM_PLACEHOLDER);

    // Escape all regex meta-characters so the path is treated literally
    const escapedPath = pathWithPlaceholders.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Replace placeholders with capture groups and escape forward slashes
    const regexPath = escapedPath.replace(new RegExp(PARAM_PLACEHOLDER, 'g'), '([^/]+)').replace(/\//g, '\\/');

    return new RegExp(`^${regexPath}$`);
  }

  /**
   * Extract parameter names from path
   */
  private extractParamNames(path: string): string[] {
    const matches = path.match(/:[^/]+/g) || [];
    return matches.map(m => m.slice(1)); // Remove the leading ':'
  }

  async registerCustomApiRoutes(): Promise<void> {
    const routes = await this.registerSchemaApiRoutes();
    if (!(await this.buildCustomRouteHandler(routes))) return;

    const server = this;
    this.app.use(async function mastraCustomRouteDispatcher(ctx: Context, next: Next) {
      // Check if this request matches a protected custom route and run auth
      const path = String(ctx.path || '/');
      const method = String(ctx.method || 'GET');
      const matchedRoute = findMatchingCustomRoute(
        path,
        method,
        server.customApiRoutes ?? server.mastra.getServer()?.apiRoutes,
      );
      const shouldRunCustomRouteAuth = isProtectedCustomRoute(path, method, server.customRouteAuthConfig);
      const shouldRunCustomRouteFGA = !!matchedRoute?.route.fga;

      const customRouteAbortController = new AbortController();
      const abortCustomRoute = () => {
        customRouteAbortController.abort();
      };
      const abortCustomRouteIfOpen = () => {
        if (!ctx.res.writableEnded) {
          abortCustomRoute();
        }
      };

      ctx.res.once('close', abortCustomRouteIfOpen);
      ctx.res.once('error', abortCustomRouteIfOpen);

      try {
        if (shouldRunCustomRouteAuth || shouldRunCustomRouteFGA) {
          const serverRoute: ServerRoute = {
            method: (matchedRoute?.route.method ?? method) as any,
            path: matchedRoute?.route.path ?? path,
            responseType: 'json',
            handler: async () => {},
            requiresAuth: matchedRoute?.route.requiresAuth,
            requiresPermission: matchedRoute?.route.requiresPermission,
            fga: matchedRoute?.route.fga,
          };

          if (shouldRunCustomRouteAuth) {
            const authError = await server.checkRouteAuth(serverRoute, {
              path,
              method,
              getHeader: name => ctx.headers[name.toLowerCase()] as string | undefined,
              getQuery: name => (ctx.query as Record<string, string>)[name],
              requestContext: ctx.state.requestContext,
              request: toWebRequest(ctx),
              buildAuthorizeContext: () => toWebRequest(ctx),
            });

            if (authError) {
              if (authError.headers) {
                for (const [key, value] of Object.entries(authError.headers)) {
                  ctx.set(key, value);
                }
              }

              if (authError.error) {
                ctx.status = authError.status;
                ctx.body = { error: authError.error };
                return;
              }
            }
          }

          const requestContext = ctx.state.requestContext;
          // Check if any auth is configured (studio or server) for RBAC
          const hasAuth = server.mastra.getStudio?.()?.auth || server.mastra.getServer()?.auth;
          if (hasAuth) {
            const hasPermission = await loadHasPermission();
            if (hasPermission) {
              const userPermissions = requestContext.get('mastra__userPermissions') as string[] | undefined;
              const permissionError = server.checkRoutePermission(
                serverRoute,
                userPermissions,
                hasPermission,
                requestContext,
              );
              if (permissionError) {
                ctx.status = permissionError.status;
                ctx.body = {
                  error: permissionError.error,
                  message: permissionError.message,
                };
                return;
              }
            }
          }

          // Check FGA authorization (EE feature)
          const fgaError = await checkRouteFGA(server.mastra, serverRoute, ctx.state.requestContext, {
            ...(matchedRoute?.params ?? {}),
            ...(ctx.query as Record<string, string>),
            ...(typeof ctx.request.body === 'object' && ctx.request.body !== null
              ? (ctx.request.body as Record<string, unknown>)
              : {}),
          });
          if (fgaError) {
            ctx.status = fgaError.status;
            ctx.body = { error: fgaError.error, message: fgaError.message };
            return;
          }
        }

        const response = await server.handleCustomRouteRequest(
          `${ctx.protocol}://${ctx.host}${ctx.originalUrl || ctx.url}`,
          ctx.method,
          ctx.headers as Record<string, string | string[] | undefined>,
          ctx.request.body,
          ctx.state.requestContext,
          customRouteAbortController.signal,
        );
        if (!response) return next();
        ctx.respond = false;
        await server.writeCustomRouteResponse(response, ctx.res, customRouteAbortController.signal);
      } finally {
        ctx.res.off('close', abortCustomRouteIfOpen);
        ctx.res.off('error', abortCustomRouteIfOpen);
      }
    });
  }

  registerContextMiddleware(): void {
    this.app.use(this.createContextMiddleware());
    this.app.use(async (ctx: Context, next: Next) => {
      const path = String(ctx.path || '/');
      const method = String(ctx.method || 'GET');
      ctx.res.once('finish', () => {
        this.warnIfUnregisteredChannelWebhook(path, method, ctx.res.statusCode);
      });
      await next();
    });
  }

  registerAuthMiddleware(): void {
    // Auth is handled per-route in registerRoute() and registerCustomApiRoutes()
    // No global middleware needed
  }

  registerHttpLoggingMiddleware(): void {
    if (!this.httpLoggingConfig?.enabled) {
      return;
    }

    const server = this;

    this.app.use(async function mastraHttpLogger(ctx: Context, next: Next) {
      if (!server.shouldLogRequest(ctx.path)) {
        return next();
      }

      const start = Date.now();
      const method = ctx.method;
      const path = ctx.path;

      await next();

      const duration = Date.now() - start;
      const status = ctx.status;
      const level = server.httpLoggingConfig?.level || 'info';

      const logData: Record<string, any> = {
        method,
        path,
        status,
        duration: `${duration}ms`,
      };

      if (server.httpLoggingConfig?.includeQueryParams) {
        logData.query = ctx.query;
      }

      if (server.httpLoggingConfig?.includeHeaders) {
        const headers = { ...ctx.headers };
        const redactHeaders = server.httpLoggingConfig.redactHeaders || [];
        redactHeaders.forEach((h: string) => {
          const key = h.toLowerCase();
          if (headers[key] !== undefined) {
            headers[key] = '[REDACTED]';
          }
        });
        logData.headers = headers;
      }

      server.logger[level](`${method} ${path} ${status} ${duration}ms`, logData);
    });
  }
}
